// API Keys Management Page - Manage MCP Server access tokens

import { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardBody, Button, StatusBadge, Modal } from '../components/common';
import { formatRelativeTime } from '../utils/dateFormatters';
import { apiKeysService } from '../services/apiKeys';
import type { ApiKey, ApiKeyCreated, MCPServer } from '../types/api';
import { KeyIcon, PlusIcon, RefreshIcon, CopyIcon, CheckIcon } from '../components/icons';
import styles from './ApiKeysPage.module.css';

// Truncate email for display
function truncateEmail(email: string | null): string {
  if (!email) return 'Unknown';
  const [name, domain] = email.split('@');
  if (name.length > 10) {
    return `${name.slice(0, 10)}...@${domain}`;
  }
  return email;
}

export function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createRole, setCreateRole] = useState('user');
  const [createNotes, setCreateNotes] = useState('');
  const [selectedServers, setSelectedServers] = useState<string[]>([]);
  const [createError, setCreateError] = useState<string | null>(null);

  // Key created success modal
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [keysData, serversData] = await Promise.all([
        apiKeysService.list(showInactive),
        apiKeysService.getMcpServers(),
      ]);
      setKeys(keysData.keys);
      setServers(serversData.servers);
    } catch (err) {
      console.error('Failed to fetch API keys data:', err);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    if (!createName.trim()) {
      setCreateError('Name is required');
      return;
    }
    if (selectedServers.length === 0) {
      setCreateError('Select at least one MCP server');
      return;
    }

    setActionLoading('create');
    setCreateError(null);
    try {
      const result = await apiKeysService.create({
        name: createName.trim(),
        role: createRole,
        notes: createNotes.trim() || undefined,
        allowed_servers: selectedServers,
      });
      setCreatedKey(result);
      setShowCreateModal(false);
      // Reset form
      setCreateName('');
      setCreateRole('user');
      setCreateNotes('');
      setSelectedServers([]);
      // Refresh list
      await fetchData();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setCreateError(error.response?.data?.detail || 'Failed to create API key');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (key: ApiKey) => {
    if (!confirm(`Deactivate API key "${key.name}"? It will no longer work for authentication.`)) {
      return;
    }
    setActionLoading(key.id);
    try {
      await apiKeysService.deactivate(key.id);
      await fetchData();
    } catch (err) {
      console.error('Failed to deactivate key:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (key: ApiKey) => {
    setActionLoading(key.id);
    try {
      await apiKeysService.reactivate(key.id);
      await fetchData();
    } catch (err) {
      console.error('Failed to reactivate key:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyKey = async () => {
    if (createdKey) {
      await navigator.clipboard.writeText(createdKey.raw_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleServer = (serverId: string) => {
    setSelectedServers(prev =>
      prev.includes(serverId)
        ? prev.filter(s => s !== serverId)
        : [...prev, serverId]
    );
  };

  // Filter keys by search term
  const filteredKeys = keys.filter(key => {
    const term = searchTerm.toLowerCase();
    return (
      key.name.toLowerCase().includes(term) ||
      key.key_prefix.toLowerCase().includes(term) ||
      (key.notes?.toLowerCase().includes(term) ?? false)
    );
  });

  // Stats
  const activeCount = keys.filter(k => k.is_active).length;
  const inactiveCount = keys.filter(k => !k.is_active).length;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <KeyIcon className={styles.titleIcon} />
            API Key Management
          </h1>
          <span className={styles.subtitle}>
            Manage MCP Server access tokens
          </span>
        </div>
        <div className={styles.headerActions}>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshIcon />
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <PlusIcon />
            Create API Key
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statValue}>{keys.length}</div>
            <div className={styles.statLabel}>Total Keys</div>
          </CardBody>
        </Card>
        <Card className={`${styles.statCard} ${styles.active}`}>
          <CardBody>
            <div className={styles.statValue}>{activeCount}</div>
            <div className={styles.statLabel}>Active</div>
          </CardBody>
        </Card>
        <Card className={`${styles.statCard} ${styles.inactive}`}>
          <CardBody>
            <div className={styles.statValue}>{inactiveCount}</div>
            <div className={styles.statLabel}>Inactive</div>
          </CardBody>
        </Card>
        <Card className={`${styles.statCard} ${styles.servers}`}>
          <CardBody>
            <div className={styles.statValue}>{servers.length}</div>
            <div className={styles.statLabel}>MCP Servers</div>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show Inactive Keys
        </label>
        <input
          type="text"
          placeholder="Search by name or prefix..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {/* Keys Table */}
      <Card className={styles.tableCard}>
        <CardBody className={styles.tableBody}>
          {loading ? (
            <div className={styles.loading}>Loading API keys...</div>
          ) : filteredKeys.length === 0 ? (
            <div className={styles.empty}>
              <KeyIcon className={styles.emptyIcon} />
              <p>No API keys found</p>
              {!showInactive && keys.length > 0 && (
                <p className={styles.emptyHint}>
                  Try enabling "Show Inactive Keys" or adjust your search
                </p>
              )}
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Servers</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Last Used</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredKeys.map((key) => (
                  <tr key={key.id} className={!key.is_active ? styles.inactive : ''}>
                    <td>
                      <div className={styles.keyName}>{key.name}</div>
                      <div className={styles.keyPrefix}>cm_{key.key_prefix}...</div>
                      {key.notes && (
                        <div className={styles.keyNotes}>{key.notes}</div>
                      )}
                    </td>
                    <td>
                      <div className={styles.serverBadges}>
                        {key.allowed_servers.map(serverId => {
                          const server = servers.find(s => s.id === serverId);
                          return (
                            <span key={serverId} className={styles.serverBadge}>
                              {server?.name || serverId}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      <span className={`${styles.roleBadge} ${styles[key.role.toLowerCase()]}`}>
                        {key.role.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <StatusBadge variant={key.is_active ? 'success' : 'error'}>
                        {key.is_active ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </td>
                    <td className={styles.createdBy}>
                      {truncateEmail(key.created_by_email)}
                    </td>
                    <td>{formatRelativeTime(key.last_used_at)}</td>
                    <td>
                      {key.is_active ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeactivate(key)}
                          disabled={actionLoading === key.id}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReactivate(key)}
                          disabled={actionLoading === key.id}
                        >
                          Reactivate
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {/* Server Legend */}
      <Card className={styles.legendCard}>
        <CardHeader>
          <h3>Available MCP Servers</h3>
        </CardHeader>
        <CardBody>
          <div className={styles.serverLegend}>
            {servers.map(server => (
              <div key={server.id} className={styles.legendItem}>
                <span className={styles.serverBadge}>{server.name}</span>
                <span className={styles.serverDescription}>{server.description}</span>
                <code className={styles.serverEndpoint}>{server.endpoint}</code>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New API Key"
        size="md"
      >
        <div className={styles.createForm}>
          <div className={styles.formGroup}>
            <label>Name *</label>
            <input
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="e.g., Claude Desktop, Cursor IDE"
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Role *</label>
            <select
              value={createRole}
              onChange={(e) => setCreateRole(e.target.value)}
              className={styles.select}
            >
              <option value="user">USER</option>
              <option value="admin">ADMIN</option>
              <option value="manager">MANAGER</option>
              <option value="readonly">READONLY</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>MCP Servers * <span className={styles.hint}>(select one or more)</span></label>
            <div className={styles.serverCheckboxes}>
              {servers.map(server => (
                <label key={server.id} className={styles.serverCheckbox}>
                  <input
                    type="checkbox"
                    checked={selectedServers.includes(server.id)}
                    onChange={() => toggleServer(server.id)}
                  />
                  <div className={styles.serverCheckboxContent}>
                    <span className={styles.serverCheckboxName}>{server.name}</span>
                    <span className={styles.serverCheckboxDesc}>{server.description}</span>
                    <code className={styles.serverCheckboxEndpoint}>{server.endpoint}</code>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>Notes <span className={styles.hint}>(optional)</span></label>
            <textarea
              value={createNotes}
              onChange={(e) => setCreateNotes(e.target.value)}
              placeholder="Describe what this API key will be used for..."
              className={styles.textarea}
              rows={3}
            />
          </div>

          {createError && (
            <div className={styles.error}>{createError}</div>
          )}

          <div className={styles.formActions}>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={actionLoading === 'create'}
            >
              {actionLoading === 'create' ? 'Creating...' : 'Create Key'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Key Created Success Modal */}
      <Modal
        isOpen={!!createdKey}
        onClose={() => setCreatedKey(null)}
        title="API Key Created!"
        size="md"
      >
        {createdKey && (
          <div className={styles.successModal}>
            <div className={styles.warning}>
              <strong>Copy this key now!</strong> You won't be able to see it again.
            </div>

            <div className={styles.keyDisplay}>
              <code className={styles.rawKey}>{createdKey.raw_key}</code>
              <Button
                variant={copied ? 'primary' : 'outline'}
                size="sm"
                onClick={handleCopyKey}
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>

            <div className={styles.usageSection}>
              <h4>Usage</h4>
              <div className={styles.usageExample}>
                <code>X-API-Key: {createdKey.raw_key.slice(0, 15)}...</code>
                <span>or</span>
                <code>Authorization: Bearer {createdKey.raw_key.slice(0, 15)}...</code>
              </div>
            </div>

            <div className={styles.serverAccess}>
              <h4>Server Access</h4>
              <div className={styles.serverAccessList}>
                {createdKey.allowed_servers.map(serverId => {
                  const server = servers.find(s => s.id === serverId);
                  return (
                    <div key={serverId} className={styles.serverAccessItem}>
                      <span className={styles.serverBadge}>{server?.name || serverId}</span>
                      <code>{server?.endpoint || `/mcp/${serverId}/mcp`}</code>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={styles.formActions}>
              <Button variant="primary" onClick={() => setCreatedKey(null)}>
                Done
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
